import React, { useContext, useEffect, useState } from "react";
import { useFieldArray, useFormContext, Controller } from "react-hook-form";
import { getOptions } from "../FormComponents/formSelectOptions";
import ReactSelect from "react-select";
import { BiographyStore } from "../../../stateManager/stores/BiographyStore";
import { updateFormData } from "../../../stateManager/actions/BiographyActions";
import NoDataProvidedMessage from "./NoDataProvidedMessage";
import { ArrayFieldAddButton } from "./ArrayFieldAddButton";
import { ArrayFieldUndoButton } from "./ArrayFieldUndoButton";
import { ArrayFieldDeleteButton } from "./ArrayFieldDeleteButton";
import { ArrayFieldMoveUpDownButtons } from "./ArrayFieldMoveUpDownButtons";

function OnlineResourcesArray({ classNames }) {
  const {
    control,
    getValues,
    reset,
    register,
    formState: { errors },
  } = useFormContext();

  const { fields, append, remove, swap } = useFieldArray({
    control,
    name: "online_resources",
  });

  const options = getOptions("onlineResourceTypes");
  const [displayUndoButton, setDisplayUndoButton] = useState(false);
  const { state, dispatch } = useContext(BiographyStore);
  const onlineResourcesValues = getValues("online_resources");

  useEffect(() => {
    if (JSON.stringify(onlineResourcesValues) === JSON.stringify(state.formData.online_resources)) {
      setDisplayUndoButton(false);
    } else {
      setDisplayUndoButton(true);
    }
  }, [onlineResourcesValues, state]);

  // setting type default value to []
  useEffect(() => {
    onlineResourcesValues?.forEach((onlineResourcesValue) => {
      if (!onlineResourcesValue.type) {
        onlineResourcesValue.type = [];
      }
    });
  }, [onlineResourcesValues]);

  if (state?.formData.online_resources.length === 0 && state?.isReadOnly) {
    return <NoDataProvidedMessage classNames={classNames} />;
  }

  return (
    <div className={"col-12 " + classNames}>
      {fields.map((item, index) => {
        const itemNum = index + 1;

        return (
          <div className="row g-2 field-array-row" key={item.id}>
            <div className="col-md-9 field-column">
              <div className="sub-field-array-style row g-2 mb-3" id={item.id}>
                <p className="subTitle">{`Online resource ` + itemNum}</p>
                <div className="col-6" id={`online_resources[${index}].title-div`}>
                  <input
                    id={`online_resources[${index}].title`}
                    name={`online_resources[${index}].title`}
                    {...register(`online_resources[${index}].title`)}
                    placeholder="Title*"
                    className="form-control"
                    readOnly={state.isReadOnly}
                  />
                  {errors?.online_resources?.[index]?.title && (
                    <span className="text-danger" data-testid="error-message">
                      {errors?.online_resources[index].title.message}
                    </span>
                  )}
                </div>
                <div className="col-6" id={`online_resources[${index}].url-div`}>
                  <input
                    id={`online_resources[${index}].url`}
                    name={`online_resources[${index}].url`}
                    {...register(`online_resources[${index}].url`)}
                    placeholder="Url"
                    className="form-control"
                    readOnly={state.isReadOnly}
                  />
                </div>
                <div className="col-12" id={`online_resources[${index}].description-div`}>
                  <input
                    id={`online_resources[${index}].description`}
                    name={`online_resources[${index}].description`}
                    {...register(`online_resources[${index}].description`)}
                    placeholder="Description"
                    className="form-control"
                    readOnly={state.isReadOnly}
                  />
                </div>
                <div className="col-12">
                  <Controller
                    name={`online_resources[${index}].type`}
                    control={control}
                    render={({ field: { name, value = [], onChange } }) => (
                      <ReactSelect
                        isClearable
                        isMulti
                        placeholder="Select Type"
                        isDisabled={state.isReadOnly}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        value={options.filter((option) => value?.includes(option?.value))}
                        name={name}
                        inputId={`${name}-input`} 
                        aria-label={name}
                        id={name}
                        options={options}
                        onChange={(e) => {
                          const newValue = e.map((i) => i.value);
                          onChange(newValue);
                        }}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            {!state?.isReadOnly && (
              <div className="col-md-3">
                <div className="row flex-row p-0">
                  <ArrayFieldDeleteButton
                    dataTestId={`online-resources-delete-button-${index}`}
                    onClick={() => {
                      updateFormData(getValues(), dispatch);
                      remove(index);
                    }}
                  />
                  <ArrayFieldMoveUpDownButtons
                    onDownButtonClick={() => swap(index, index + 1)}
                    onUpButtonClick={() => swap(index, index - 1)}
                    index={index}
                    fields={fields}
                    upButtonDataTestId={`online-resources-${index}-up`}
                    downButtonDataTestId={`online-resources-${index}-down`}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}

      {!state?.isReadOnly && (
        <ArrayFieldAddButton
          dataTestId={"online-resources-add-button"}
          onClick={() => {
            append({ title: "", url: "", description: "", type: null });
            updateFormData(getValues(), dispatch);
          }}
        />
      )}
      {displayUndoButton && (
        <ArrayFieldUndoButton
          dataTestId={"online-resources-undo-button"}
          getValues={getValues}
          append={append}
          reset={reset}
        />
      )}
    </div>
  );
}

export default OnlineResourcesArray;
